import { navigateTo } from "#app"
import { toast } from "vue-sonner"
import { clientSdk } from "~/lib/platform/sdk"
import { isReadCommand, isServiceUnavailableError, resilientInvoke, SERVICE_UNAVAILABLE_MESSAGE } from "~/lib/platform/retry"

// Single choke point for every backend command call in the app (component → composable/store →
// invoke → either Tauri's IPC bridge or a direct REST call, per `~/lib/platform/ClientSdk.ts` —
// see CLAUDE.md) — this is where a session that expired mid-use gets caught and turned into a
// redirect to the login page, instead of every composable/store having to check for it
// individually. Under Tauri, every `#[tauri::command]` maps its tonic::Status into a plain string
// before it reaches us (see commands/*.rs), so detection here is necessarily string-based:
// `SessionManager::get_access_token()` returns the literal "Not authenticated" when there's no
// session, and any command that stringifies a tonic::Status with `.to_string()` (rather than just
// `.message()`) includes the status code's name, so a server-rejected/expired token surfaces as
// "...Unauthenticated...` too. `BrowserClientSdk`'s `RestApiError` deliberately reuses the same
// "Unauthenticated"/"401" wording for a 401 response so this same check catches both.
const AUTH_ERROR_PATTERN = /not authenticated|unauthenticated|\b401\b/i

// Session expiry during startup often fails several in-flight calls (SignalR connect, message
// fetch, ...) at once — this collapses that burst into a single redirect instead of re-navigating
// once per failed call.
let redirectingToLogin = false

function isAuthError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return AUTH_ERROR_PATTERN.test(message)
}

async function redirectToLogin() {
  if (redirectingToLogin) return
  redirectingToLogin = true
  try {
    // Dynamic import to avoid a static circular dependency — every store that imports `invoke`
    // from this module would otherwise be part of a cycle through `stores/user.ts`.
    const { useUserStore } = await import("~/stores/user")
    useUserStore().clearUser()
  } finally {
    await navigateTo("/", { replace: true })
    redirectingToLogin = false
  }
}

// Retry/backoff for an overloaded or restarting backend, and in-flight dedup of identical reads,
// are applied here for every command — see `~/lib/platform/retry.ts` for the policy and the one
// list of commands that may be repeated automatically. A transient failure is never an auth
// failure: it must not log the user out, and whatever is already on screen stays there.
export async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  try {
    return await resilientInvoke<T>(cmd, args, () => clientSdk.invoke<T>(cmd, args))
  } catch (error) {
    if (isServiceUnavailableError(error)) {
      // Reads are mostly fire-and-forget loaders that swallow their errors, so this is the only
      // place the user would hear about one that kept failing after its retries. Writes are left
      // to their caller, which already reports its own failures. The fixed id collapses a burst
      // of failed reads into a single toast.
      if (isReadCommand(cmd)) toast.warning(SERVICE_UNAVAILABLE_MESSAGE, { id: "service-unavailable" })
    } else if (isAuthError(error)) {
      void redirectToLogin()
    }
    throw error
  }
}
